import React, { useCallback, useState, useMemo } from 'react';

import { useTranslate } from '@src/core/hooks';
import { Scheduling, RescheduleScheduling, FeaturedTariffPreviewMode } from '@src/core/buyerApiClient';
import CalendarLegend from '@src/components/reusable/Calendar/CalendarLegend';
import {
  initialMonth,
  monthNames,
  MonthTuple,
  mountInterval,
  changeMonth,
  dateToObj,
  mergeIntervalWithDates,
} from '@src/common/calendar';
import CalendarHeader from './CalendarHeader';
import CalendarMonthView from './CalendarMonthView';
import CalendarYearView from './CalendarYearView';
import { Container } from './styles';

type DateProps = {
  [key: string]: {
    availables: Scheduling[] | RescheduleScheduling[];
    unavailables: Scheduling[] | RescheduleScheduling[];
  };
};

type Props = {
  dates: DateProps;
  pending: boolean;
  onDateSelect: (date: string) => void;
  tariffToShowThePrice?: FeaturedTariffPreviewMode | null;
  /**
   * Controlled visible month: the owner (e.g. useScheduleDatesLoader) holds the state, the
   * auto-load loop and the fetching. Used by the product adders.
   */
  monthTuple?: MonthTuple;
  onMonthTupleChange?: (next: MonthTuple) => void;
  /** Uncontrolled seed when monthTuple isn't provided (Reschedule has all dates upfront). */
  defaultDateReference?: string;
};

const Calendar = (props: Props) => {
  const {
    dates,
    pending,
    onDateSelect,
    tariffToShowThePrice,
    monthTuple: controlledMonthTuple,
    onMonthTupleChange,
    defaultDateReference,
  } = props;

  const isControlled = controlledMonthTuple != null;

  const { i18n } = useTranslate();
  const [mode, setMode] = useState<'month' | 'year'>('month');
  const [internalMonthTuple, setInternalMonthTuple] = useState<MonthTuple>(() => {
    if (defaultDateReference) {
      const [year, month] = dateToObj(new Date(defaultDateReference));
      return [year, month];
    }
    return initialMonth();
  });

  const monthTuple = controlledMonthTuple ?? internalMonthTuple;

  const setMonthTuple = (next: MonthTuple) => {
    if (isControlled) {
      onMonthTupleChange?.(next);
    } else {
      setInternalMonthTuple(next);
    }
  };

  const interval = useMemo(() => mountInterval(monthTuple), [monthTuple]);

  const filteredDates = mergeIntervalWithDates(interval, dates);
  const [year, month] = monthTuple;

  const calculateNextOrPreviousMonth = (step: number) => {
    switch (mode) {
      case 'month':
        setMonthTuple(changeMonth(monthTuple, step));
        break;
      case 'year':
        setMonthTuple([year + step, month]);
        break;
    }
  };

  const handlePrevious = () => calculateNextOrPreviousMonth(-1);
  const handleNext = () => calculateNextOrPreviousMonth(1);

  const handleMonthVisualization = (nextTuple: MonthTuple) => {
    setMode('month');
    setMonthTuple(nextTuple);
  };

  const handleDateSelect = useCallback((date: string) => onDateSelect(date), [onDateSelect]);

  return (
    <Container>
      <CalendarLegend />

      <CalendarHeader onNext={handleNext} onPrevious={handlePrevious} onModeChange={() => setMode('year')}>
        {mode === 'month' ? `${i18n(monthNames[month - 1])} ${year}` : year}
      </CalendarHeader>

      {mode == 'month' ? (
        <CalendarMonthView
          pending={pending}
          monthTuple={monthTuple}
          filteredDates={filteredDates}
          onSelectDate={handleDateSelect}
          tariffToShowThePrice={tariffToShowThePrice}
        />
      ) : null}

      {mode == 'year' ? <CalendarYearView year={year} onMonthClick={handleMonthVisualization} /> : null}
    </Container>
  );
};

export type { DateProps as CalendarDateProps };
export default Calendar;
